import { api } from './client';
import type { FormSchema } from '@/domain/schemaForm';

/** 这条收款人是怎么来的。界面上合成一列显示，不拆成一堆列。 */
export interface PayeeOrigin {
  kind: 'internal' | 'external';
  createdByName: string | null;
  linkTitle: string | null;
  linkCreatedByName: string | null;
  filledByEmail: string | null;
  filledAt: string | null;
}

/**
 * 「打款情况」的一个桶：某币种、某一类状态下的金额与笔数。
 *
 * 🔴 `amount` 是**字符串**，后端在 Postgres 的 numeric 里加完直接给出。
 * 任何 `Number(amount)` 都会让它失真 —— 这是财务判断钱在哪的依据。
 */
export type PayoutBucketKey = 'PENDING' | 'IN_FLIGHT' | 'UNKNOWN' | 'PAID' | 'FAILED';

export interface PayoutBucket {
  key: PayoutBucketKey;
  currency: string;
  amount: string;
  count: number;
}

export interface Payee {
  id: string;
  organizationId: string;
  /** 给人念、抄、发消息用的短码，如 `7K3M9QR2`。全局唯一。 */
  shortId: string;
  airwallexBeneficiaryId: string;
  nickname: string | null;
  /** 收款人自己填的联系邮箱，仅展示用，不参与打款。 */
  contactEmail: string | null;
  origin: PayeeOrigin;
  /** 围绕这个收款人的钱现在在哪。只含非零的桶，没打过款时是空数组。 */
  payoutSummary: PayoutBucket[];
  accountName: string | null;
  entityType: string | null;
  transferMethod: string | null;
  bankCountryCode: string | null;
  accountCurrency: string | null;
  localClearingSystem: string | null;
  /** DRAFT = 只在本地；SYNCED = 已推送到 Airwallex（见 PRODUCT.md §4.2）。 */
  syncStatus: 'DRAFT' | 'SYNCED';
  /** 用户在动态表单里填的全部字段（path → value）。详情页展示的就是它。 */
  formValues: Record<string, unknown>;
  snapshot: Record<string, unknown>;
  createdAt: string;
}

export interface PayeeFilter {
  keyword?: string;
  entityType?: string[];
  bankCountryCode?: string[];
  accountCurrency?: string[];
  transferMethod?: string[];
  syncStatus?: Array<'DRAFT' | 'SYNCED'>;
  createdFrom?: string;
  createdTo?: string;
}

/**
 * 筛选下拉的选项来源：本组织**已有的收款人**里出现过的值。
 * 不在前端硬编码国家 / 币种表 —— 那份表由 Airwallex 维护，抄一份过来就得自己跟进它的变化。
 */
export interface PayeeFacets {
  bankCountryCodes: string[];
  accountCurrencies: string[];
  transferMethods: string[];
}

export interface SchemaQuery {
  bank_country_code?: string;
  account_currency?: string;
  transfer_method?: string;
  entity_type?: string;
  local_clearing_system?: string;
  country_code?: string;
}

export async function getPayeeSchema(query: SchemaQuery) {
  const { data } = await api.post<FormSchema>('/payees/schema', query);
  return data;
}

export interface InstitutionQuery {
  keyword: string;
  bank_country_code: string;
  account_currency: string;
  transfer_method: string;
  entity_type: string;
  local_clearing_system?: string;
}

export async function searchInstitutions(query: InstitutionQuery) {
  const { data } = await api.post<{ items: Array<{ label: string; value: string }>; truncated: boolean }>(
    '/payees/institutions',
    query,
  );
  return data;
}

/** AI 快速录入识别出的一个字段。`label` 是 Airwallex 下发的字段名（英文），预览里直接显示。 */
export interface AiExtractedField {
  path: string;
  label: string;
  value: string;
}

export interface AiRejectedField extends AiExtractedField {
  /** not_an_option = 不在可选项里；pattern_mismatch = 不符合字段正则 */
  reason: 'not_an_option' | 'pattern_mismatch';
}

export interface AiExtractResult {
  sourceRecordId?: string;
  /** 定下来的「主体类型 × 国家 × 币种 × 汇款方式」等条件（schema 查询参数的形态）。 */
  conditions: Record<string, string>;
  /** 清洗后的 path → 值，可以直接作为表单值。 */
  values: Record<string, string>;
  filled: AiExtractedField[];
  /** 这版 schema 里可见且必填、但没识别出来的字段 —— 要人来补。 */
  missing: Array<{ path: string; label: string }>;
  rejected: AiRejectedField[];
  /** 当次动态 schema 的可见字段，供 AI 弹窗右侧渲染对照表单。 */
  fields: Array<{
    path: string;
    label: string;
    type: string;
    required: boolean;
    enabled: boolean;
    default: string | null;
    options: Array<{ value: string; label: string }>;
    description?: string;
    example?: string;
    pattern?: string;
  }>;
  /** input = 原始数据；ai_inferred = AI 推断补充；ai_search = AI 联网搜索补齐。 */
  sources: Record<string, 'input' | 'ai_inferred' | 'ai_search'>;
  searchStatus: 'not_needed' | 'completed' | 'failed';
}

/** 与服务端一致的输入上限。 */
export const AI_EXTRACT_MAX_CHARS = 20_000;

/**
 * AI 快速录入：任意文本 → 表单字段值。
 * 🔴 文本会经服务端发给第三方模型（PRODUCT.md §4.9）；结果只是**建议**，仍要走 validate / create。
 */
export async function aiExtractPayee(text: string) {
  const { data } = await api.post<AiExtractResult>('/payees/ai-extract', { text });
  return data;
}

export async function validatePayee(values: Record<string, string>) {
  const { data } = await api.post<{ ok: true }>('/payees/validate', { values });
  return data;
}

export async function createPayee(values: Record<string, string>, sourceRecordId?: string) {
  const { data } = await api.post<Payee>('/payees/create', { values, sourceRecordId });
  return data;
}

export async function listPayees(filter: PayeeFilter = {}) {
  const { data } = await api.post<{ items: Payee[]; facets: PayeeFacets }>('/payees/list', filter);
  return data;
}

export async function getPayee(id: string) {
  const { data } = await api.post<Payee>('/payees/get', { id });
  return data;
}

/**
 * 按短 id 查收款人。别人把 `7K3M9QR2` 发过来时用它。
 * 🔴 没有删除接口：收款人一旦创建就永久保留（见 PRODUCT.md §4.3）。
 */
export async function getPayeeByShortId(shortId: string) {
  const { data } = await api.post<Payee>('/payees/by-short-id', { shortId });
  return data;
}
